import type {
  InfoPratiques as MobileInfoPratiques,
  InfoPratiquesId,
  InfoPratiquesSection,
  SocialMediaLinks,
} from '@code-troopers/mobile-preview/types/grab/types'
import axios from 'axios'

import { getTargetCategory } from '@/shared/deeplink'
import type {
  EditorImage,
  EditorInfoPratiques,
} from '@/shared/types/applicationData.type'

import { IdsService, weightService } from '../../shared/service/utils.service'
import { fromEntries } from '../../utils'
import { type MenuElement } from '../menuEntries/MenuEntry.type'
import { type PageContentWithElements } from '../menuEntries/pageSetup/pageContent.type'
import { type DeeplinkTarget } from '../notifications/notifications.service'
import {
  type Button,
  type EditorButtonTarget,
  type Info,
  type Section,
} from './Infos'
import { type StateInfo, type StateSection } from './infos.store'

// `IconProp` lives in `@fortawesome/fontawesome-svg-core` which is a
// transitive dep of the mobile types but not a direct backoffice dep — pull
// the type out via indexed access so we can name it without installing the
// package here.
type IconProp = MobileInfoPratiques['icon']

type EditorButton = {
  names: Button['names']
  icon: Button['icon']
  target: EditorButtonTarget
}
type EditorSectionLike = Pick<
  Section,
  'names' | 'weight' | 'descriptions' | 'links' | 'expand'
> & {
  buttons: Record<string, EditorButton> | EditorButton[]
}

// Accept both the saved `StateInfo` shape (sections as a record) and the
// half-filled `Partial<StateInfo>` the Adding page uses while the user is
// still typing. Missing fields fall back to safe defaults below.
export type EditorInfoLike = {
  _id?: string
  names?: Info['names']
  weight?: number
  icon?: string
  image?: string
  copyright?: string
  sections?: Record<string, EditorSectionLike> | EditorSectionLike[]
}

// Button targets are stored as the legacy string read by the app (resolveActionTarget): an https
// url, or chapito://<base>?id=<param>. URL params (webview, extern, weezevent...) are encoded so
// their own ?/&/= survive; entity ids stay raw since the app splits id lists on ','.

// the scheme is the festival env, which may contain a hyphen (magic-odyssey...)
const DEEPLINK_PATTERN = /^([\w-]+):\/\/(\w+)\??(.*)$/

export function parseButtonTarget(target: string): EditorButtonTarget {
  const match = DEEPLINK_PATTERN.exec(target)
  if (!match || match[1] === 'http' || match[1] === 'https') {
    return { type: 'EXTERNAL_URL', url: target }
  }
  const deeplinkBase = match[2] as DeeplinkTarget
  const deeplinkParam = new URLSearchParams(match[3]).get('id') ?? ''
  // the browser deeplink is not offered for buttons: same behavior as an external url
  if (deeplinkBase === 'extern')
    return { type: 'EXTERNAL_URL', url: deeplinkParam }
  return { type: 'DEEPLINK', deeplinkBase, deeplinkParam }
}

export function formatButtonTarget(target: EditorButtonTarget): string {
  if (target.type === 'EXTERNAL_URL') return target.url
  const { deeplinkBase, deeplinkParam } = target
  if (!deeplinkParam) return `chapito://${deeplinkBase}`
  const param =
    getTargetCategory(deeplinkBase) === 'text-input'
      ? encodeURIComponent(deeplinkParam)
      : deeplinkParam
  return `chapito://${deeplinkBase}?id=${param}`
}

// Adapters from the editor model to the mobile preview's InfoPratiques.
// They accept both Info (sections as array) and StateInfo (sections as record).

function adaptButton(b: EditorButton): InfoPratiquesSection['buttons'][number] {
  return {
    name: b.names.fr,
    target: formatButtonTarget(b.target),
    icon: b.icon as IconProp,
  }
}

function adaptSection(s: EditorSectionLike): InfoPratiquesSection {
  return {
    name: s.names.fr || '\u200B',
    weight: s.weight,
    description: s.descriptions.fr || '<p><br></p>',
    buttons: Object.values(s.buttons).map(adaptButton),
    // Editor's `SocialLink` keys match `SocialMediaLinks`; the values are
    // wider (`LinkEntry`) but the runtime preview reads the same fields, so
    // a single-step cast is sufficient.
    links: (s.links ?? undefined) as SocialMediaLinks | undefined,
    expand: s.expand,
  }
}

export function adaptInfo(
  info: EditorInfoLike,
  image?: EditorImage,
): EditorInfoPratiques {
  return {
    _id: (info._id ?? 'create') as InfoPratiquesId,
    name: info.names?.fr ?? '',
    weight: info.weight ?? 0,
    icon: (info.icon ?? '') as IconProp,
    sections: info.sections
      ? Object.values(info.sections).map(adaptSection)
      : [],
    image: image ?? info.image,
    copyright: info.copyright,
  }
}

const convertToAPI = (info: StateInfo): Info => ({
  ...info,
  sections: Object.values(info.sections).map((section) => ({
    ...section,
    buttons: Object.values(section.buttons).map((b) => ({
      ...b,
      target: formatButtonTarget(b.target),
    })),
  })),
})

const convertToStateInfo = (info: Info): StateInfo => {
  const sections = fromEntries(
    info.sections.map(
      (section) =>
        [IdsService.generateId(), convertToStateSection(section)] as const,
    ),
  )
  return { ...info, sections }
}

const convertToStateSection = (section: Section): StateSection => {
  const buttons = fromEntries(
    section.buttons.map(
      (button) =>
        [
          IdsService.generateId(),
          {
            ...button,
            target: parseButtonTarget(button.target),
          },
        ] as const,
    ),
  )
  return {
    ...section,
    buttons,
  }
}

export type InfosPratiqueUsage = {
  pageContents: PageContentWithElements[]
  navBarElements: MenuElement[]
}

export const InfosService = {
  async getAll(): Promise<StateInfo[]> {
    const { data } = await axios.get<Info[]>('/api/infosPratiques')
    return weightService.formatWeight(data).map(convertToStateInfo)
  },

  async updateInfo(info: StateInfo): Promise<StateInfo> {
    const { data } = await axios.put<Info>(
      `/api/infosPratiques/${info._id}`,
      convertToAPI(info),
    )
    return convertToStateInfo(data)
  },

  async delete(id: string): Promise<void> {
    await axios.delete(`/api/infosPratiques/${id}`)
  },

  async addInfo(info: StateInfo): Promise<StateInfo> {
    const { data } = await axios.post<Info>(
      '/api/infosPratiques',
      convertToAPI(info),
    )
    return convertToStateInfo(data)
  },

  async checkUsage(ids: string[]): Promise<InfosPratiqueUsage> {
    const { data } = await axios.get<InfosPratiqueUsage>(
      '/api/infosPratiques/check-usage',
      { params: { ids: ids.join(',') } },
    )
    return data
  },

  async getInfoById(id: string): Promise<StateInfo> {
    const { data } = await axios.get<Info>(`/api/infosPratiques/${id}`)
    return convertToStateInfo(data)
  },
}
